/**
 * SyncTab — Chart of Accounts + Tax Rates "Sync now" mini-cards with
 * last-synced text, plus a Sync History table. Lifted from the Xero card that
 * used to live in Settings → Organization.
 */
import { BookOpenCheck, Loader2, Receipt, RefreshCw } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { formatDateTime, formatRelative } from '../../lib/format';

function lastSyncedLabel(
  logs: Array<{ sync_type: string; started_at: string }> | undefined,
  syncType: string
): string {
  const match = logs?.find((log) => log.sync_type === syncType);
  return match ? `Updated ${formatRelative(match.started_at)}` : 'Never synced';
}

export function SyncTab() {
  const utils = trpc.useUtils();
  const syncLogsQuery = trpc.xero.getSyncLogs.useQuery({ limit: 20 }, { staleTime: 30_000 });

  const syncAccounts = trpc.xero.syncAccounts.useMutation({
    onSuccess: async (result) => {
      await Promise.all([
        utils.xero.getAccounts.invalidate(),
        utils.xero.getSyncLogs.invalidate(),
      ]);
      toast.success(`Accounts synced (${result.synced})`);
    },
    onError: (error) => toast.error(error.message || 'Failed to sync chart of accounts'),
  });

  const syncTaxRates = trpc.xero.syncTaxRates.useMutation({
    onSuccess: async (result) => {
      await utils.xero.getSyncLogs.invalidate();
      toast.success(`Tax rates synced (${result.synced})`);
    },
    onError: (error) => toast.error(error.message || 'Failed to sync tax rates'),
  });

  const logs = syncLogsQuery.data;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Chart of Accounts */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <BookOpenCheck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <div>
                <CardTitle className="text-base">Chart of accounts</CardTitle>
                <CardDescription>{lastSyncedLabel(logs, 'accounts')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs gap-1.5"
              onClick={() => syncAccounts.mutate()}
              disabled={syncAccounts.isPending}
            >
              {syncAccounts.isPending ? (
                <Loader2
                  className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {syncAccounts.isPending ? 'Syncing accounts' : 'Sync now'}
            </Button>
          </CardContent>
        </Card>

        {/* Tax Rates */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Receipt className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <div>
                <CardTitle className="text-base">Tax rates</CardTitle>
                <CardDescription>{lastSyncedLabel(logs, 'tax_rates')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs gap-1.5"
              onClick={() => syncTaxRates.mutate()}
              disabled={syncTaxRates.isPending}
            >
              {syncTaxRates.isPending ? (
                <Loader2
                  className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {syncTaxRates.isPending ? 'Syncing tax rates' : 'Sync now'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Sync history */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sync history</CardTitle>
          <CardDescription>Recent chart-of-accounts and tax-rate syncs.</CardDescription>
        </CardHeader>
        <CardContent>
          {syncLogsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading sync history…</p>
          ) : (logs?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No sync activity yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table className="min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 py-1.5">Type</TableHead>
                    <TableHead className="px-3 py-1.5">Started</TableHead>
                    <TableHead className="px-3 py-1.5 text-right">Items</TableHead>
                    <TableHead className="px-3 py-1.5">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(logs ?? []).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="px-3 py-1.5 text-xs font-medium capitalize">
                        {log.sync_type.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(log.started_at)}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right text-xs tabular-nums">
                        {log.items_synced ?? 0}
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <Badge
                          variant={
                            log.status === 'completed'
                              ? 'success'
                              : log.status === 'completed_with_errors'
                                ? 'warning'
                                : log.status === 'failed'
                                  ? 'destructive'
                                  : 'secondary'
                          }
                          className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        >
                          {log.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
